import { useCallback, useMemo, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import { SelectButton } from 'primereact/selectbutton'
import { TabView, TabPanel } from 'primereact/tabview'
import { Panel } from 'primereact/panel'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Tag } from 'primereact/tag'
import { Dialog } from 'primereact/dialog'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { OverlayPanel } from 'primereact/overlaypanel'
import { Message } from 'primereact/message'
import { ProgressBar } from 'primereact/progressbar'
import type {
  DetailedOptimizerResponse,
  Order_Output as Order,
  ScheduledTask_Output,
  OrderMatchResult,
  ScheduleWarning,
  TaskAllocation,
  Product_Output,
  MachineVariant_Output,
  ProductionSequenceItem,
  StaffMember,
} from '@/api'
import type { WeekOption, ShiftAssignments, ShiftType, ExcelReloadDialogState, ExcelWriteDialogState } from '../hooks/useDetailedPlanFlow'
import { MACHINE_ORDER, SHIFT_LABELS } from '../hooks/useDetailedPlanFlow'
import { openShiftPrintWindow } from './PrintView'
import StaffAssignmentEditDialog, { type SlotsByShift, type StaffAssignmentEditTarget } from './StaffAssignmentEditDialog'

interface TaskDetailInfo {
  task: ScheduledTask_Output
  product: Product_Output | undefined
  variant: MachineVariant_Output | undefined
}

interface OptimizerResultViewProps {
  result: DetailedOptimizerResponse
  week: WeekOption
  machineIds: string[]
  products: Product_Output[]
  orders: Order[]
  assignments: ShiftAssignments
  // 直近の最適化に渡したシフト割当（成形機ごとの「変更あり」表示に使う）
  optimizedAssignments: ShiftAssignments | null
  // 最適化後にシフト割当が変更され、結果が古くなっている可能性があるか
  hasPendingShiftChanges: boolean
  staffList: StaffMember[]
  onBack: () => void
  onReoptimize: () => void
  onUpdateMachineDayAssignments: (date: string, machineId: string, slotsByShift: SlotsByShift) => void
  excelReloadDialog: ExcelReloadDialogState
  excelWriteDialog: ExcelWriteDialogState
  isOptimizing: boolean
}

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

const formatDuration = (seconds: number): string => {
  const totalMinutes = Math.round(seconds / 60)
  if (totalMinutes >= 60) {
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return m > 0 ? `${h}h${String(m).padStart(2, '0')}m` : `${h}h`
  }
  return `${totalMinutes}m`
}

type ChangeType = 'unchanged' | 'split' | 'pulled_forward' | 'pushed_back'

const getChangeType = (task: ScheduledTask_Output): ChangeType => {
  if (task.is_split) return 'split'
  if (task.original_date === task.scheduled_date) return 'unchanged'
  if (task.scheduled_date < task.original_date) return 'pulled_forward'
  return 'pushed_back'
}

const ORDER_STATUS_SEVERITY: Record<string, 'info' | 'warning' | 'danger'> = {
  fulfilled: 'info',
  partial: 'warning',
  unmatched: 'danger',
}
const ORDER_STATUS_LABEL: Record<string, string> = {
  fulfilled: '充足',
  partial: '一部充足',
  unmatched: '未処理',
}

type DayRow = (ScheduledTask_Output & { machineGroup: string; isIdle?: false }) | {
  task_id: string
  machine_id: string
  machineGroup: string
  isIdle: true
}

const OptimizerResultView: React.FC<OptimizerResultViewProps> = ({
  result,
  week,
  machineIds,
  products,
  orders,
  assignments,
  optimizedAssignments,
  hasPendingShiftChanges,
  staffList,
  onBack,
  onReoptimize,
  onUpdateMachineDayAssignments,
  excelReloadDialog,
  excelWriteDialog,
  isOptimizing,
}) => {
  const [orderDetailResult, setOrderDetailResult] = useState<OrderMatchResult | null>(null)
  const [orderStatusFilter, setOrderStatusFilter] = useState<string | null>(null)
  const [skuSearch, setSkuSearch] = useState('')
  const [viewMode, setViewMode] = useState<'optimized' | 'baseline'>('optimized')
  const [taskDetailInfo, setTaskDetailInfo] = useState<TaskDetailInfo | null>(null)
  const [daySummaryDate, setDaySummaryDate] = useState<string | null>(null)
  const [staffEditTarget, setStaffEditTarget] = useState<StaffAssignmentEditTarget | null>(null)
  const [debugLogSearch, setDebugLogSearch] = useState('')
  // 特記は非制御(uncontrolled)にして入力毎の再レンダリングを避ける。印刷時に ref から値を読む。
  const noteRef = useRef<HTMLTextAreaElement>(null)

  const hasBaseline = (result.baseline_tasks?.length ?? 0) > 0

  // Active data based on view mode
  const activeTasks = useMemo(
    () => (viewMode === 'baseline' && hasBaseline ? result.baseline_tasks! : result.scheduled_tasks),
    [viewMode, hasBaseline, result.baseline_tasks, result.scheduled_tasks],
  )
  const activeOrderResults = useMemo(
    () => (viewMode === 'baseline' && hasBaseline ? result.baseline_order_results! : result.order_results),
    [viewMode, hasBaseline, result.baseline_order_results, result.order_results],
  )
  // 生産順・段替え番号（サーバ算出）。帳票の並び順と①②表示に使う。
  const sequencesByDateMachine = useMemo(() => {
    const sequences =
      viewMode === 'baseline' && hasBaseline
        ? result.baseline_production_sequences ?? []
        : result.production_sequences ?? []
    const map: Record<string, Record<string, ProductionSequenceItem[]>> = {}
    for (const seq of sequences) {
      if (!map[seq.date]) map[seq.date] = {}
      map[seq.date][seq.machine_id] = seq.items
    }
    return map
  }, [viewMode, hasBaseline, result.production_sequences, result.baseline_production_sequences])

  const activeSummary = useMemo(
    () => (viewMode === 'baseline' && hasBaseline && result.baseline_summary ? result.baseline_summary : result.summary),
    [viewMode, hasBaseline, result.baseline_summary, result.summary],
  )

  // Build product lookup
  const productBySku = useMemo(() => {
    const map: Record<string, Product_Output> = {}
    for (const p of products) {
      map[p.sku_id] = p
      if (p.machine_sheet_sku_id) map[p.machine_sheet_sku_id] = p
      for (const c of p.components) {
        map[c.sku_id] = c
      }
    }
    return map
  }, [products])

  // Build scheduled task lookup by task_id
  const taskById = useMemo(() => {
    const map: Record<string, ScheduledTask_Output> = {}
    for (const t of activeTasks) {
      map[t.task_id] = t
    }
    return map
  }, [activeTasks])

  // Build warning lookup by task_id
  const warningsByTaskId = useMemo(() => {
    const map: Record<string, ScheduleWarning[]> = {}
    for (const w of result.warnings) {
      if (w.task_id) {
        if (!map[w.task_id]) map[w.task_id] = []
        map[w.task_id].push(w)
      }
    }
    return map
  }, [result.warnings])

  // Build warning lookup by (date, machine_id) for machine header display
  const warningsByDateMachine = useMemo(() => {
    const map: Record<string, ScheduleWarning[]> = {}
    // Build task lookup from all scheduled + unscheduled for resolving task_id
    const allTaskLookup: Record<string, { machine_id: string; date: string }> = {}
    for (const t of result.scheduled_tasks) {
      allTaskLookup[t.task_id] = { machine_id: t.machine_id, date: t.scheduled_date }
    }
    for (const t of result.unscheduled_tasks) {
      allTaskLookup[t.task_id] = { machine_id: t.machine_id, date: t.date }
    }
    if (result.baseline_tasks) {
      for (const t of result.baseline_tasks) {
        if (!allTaskLookup[t.task_id]) {
          allTaskLookup[t.task_id] = { machine_id: t.machine_id, date: t.scheduled_date }
        }
      }
    }
    for (const w of result.warnings) {
      if (!w.task_id) continue
      const taskInfo = allTaskLookup[w.task_id]
      if (!taskInfo) continue
      const key = `${taskInfo.date}|${taskInfo.machine_id}`
      if (!map[key]) map[key] = []
      map[key].push(w)
    }
    return map
  }, [result.warnings, result.scheduled_tasks, result.unscheduled_tasks, result.baseline_tasks])

  const warningOverlayRef = useRef<OverlayPanel>(null)
  const [activeWarnings, setActiveWarnings] = useState<ScheduleWarning[]>([])

  // Summary KPI values
  const avgUtilization = useMemo(() => {
    const vals = Object.values(activeSummary.utilization_by_machine)
    if (vals.length === 0) return 0
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
  }, [activeSummary.utilization_by_machine])

  // Group scheduled tasks by date → machine_id
  const tasksByDateMachine = useMemo(() => {
    const map: Record<string, Record<string, ScheduledTask_Output[]>> = {}
    for (const t of activeTasks) {
      if (!map[t.scheduled_date]) map[t.scheduled_date] = {}
      if (!map[t.scheduled_date][t.machine_id]) map[t.scheduled_date][t.machine_id] = []
      map[t.scheduled_date][t.machine_id].push(t)
    }
    return map
  }, [activeTasks])

  // 24h基準の平均稼働率（日×成形機ごとの稼働時間／24h を平均）
  const avgUtilization24h = useMemo(() => {
    const values: number[] = []
    for (const date of Object.keys(tasksByDateMachine)) {
      for (const tasks of Object.values(tasksByDateMachine[date])) {
        const actualSec = tasks.reduce((sum, t) => sum + t.duration_sec, 0)
        values.push((actualSec / (24 * 3600)) * 100)
      }
    }
    if (values.length === 0) return 0
    return Math.round(values.reduce((a, b) => a + b, 0) / values.length)
  }, [tasksByDateMachine])

  // All dates that appear in scheduled tasks (union with week dates)
  const allDates = useMemo(() => {
    const dateSet = new Set<string>(week.dates)
    for (const t of activeTasks) {
      dateSet.add(t.scheduled_date)
    }
    return Array.from(dateSet).sort()
  }, [activeTasks, week.dates])

  // Sorted machine IDs for a date
  const sortedMachineIds = useCallback(
    (date: string): string[] => {
      const ids = new Set<string>(machineIds)
      for (const machineId of Object.keys(tasksByDateMachine[date] ?? {})) {
        ids.add(machineId)
      }

      return Array.from(ids).sort((a, b) => {
        const ai = MACHINE_ORDER.indexOf(a)
        const bi = MACHINE_ORDER.indexOf(b)
        const aIdx = ai === -1 ? MACHINE_ORDER.length : ai
        const bIdx = bi === -1 ? MACHINE_ORDER.length : bi
        if (aIdx !== bIdx) return aIdx - bIdx
        return a.localeCompare(b)
      })
    },
    [machineIds, tasksByDateMachine],
  )

  // Flatten tasks for a date into DataTable rows
  const buildDayRows = useCallback(
    (date: string) => {
      const rows: DayRow[] = []
      for (const machineId of sortedMachineIds(date)) {
        const tasks = tasksByDateMachine[date]?.[machineId] ?? []
        if (tasks.length === 0) {
          rows.push({
            task_id: `idle-${date}-${machineId}`,
            machine_id: machineId,
            machineGroup: machineId,
            isIdle: true,
          })
          continue
        }
        for (const task of tasks) {
          rows.push({ ...task, machineGroup: machineId })
        }
      }
      return rows
    },
    [tasksByDateMachine, sortedMachineIds],
  )

  // Build order lookup: order_id → Order
  const orderById = useMemo(() => {
    const map: Record<string, Order> = {}
    for (const o of orders) {
      map[o.order_id] = o
    }
    return map
  }, [orders])

  // Date range: week.startDate + 3 〜 week.endDate + 14 days
  const rangeStart = useMemo(() => {
    const d = new Date(week.startDate)
    d.setDate(d.getDate() + 3)
    return d.toISOString().slice(0, 10)
  }, [week.startDate])
  const rangeEnd = useMemo(() => {
    const d = new Date(week.endDate)
    d.setDate(d.getDate() + 14)
    return d.toISOString().slice(0, 10)
  }, [week.endDate])

  // Filtered summary counts (reflect the same date filter as table)
  const filteredSummary = useMemo(() => {
    const dateFiltered = activeOrderResults.filter((o) => {
      const order = orderById[o.order_id]
      if (!order) return false
      if (order.due_date < rangeStart || order.due_date > rangeEnd) return false
      return true
    })
    let fulfilled = 0
    let partial = 0
    let unmatched = 0
    for (const o of dateFiltered) {
      if (o.status === 'fulfilled') fulfilled++
      else if (o.status === 'partial') partial++
      else unmatched++
    }
    return { fulfilled, partial, unmatched }
  }, [activeOrderResults, orderById, week.startDate, rangeEnd])

  // Filtered order results: date range + status filter
  const filteredOrderResults = useMemo(() => {
    const skuLower = skuSearch.toLowerCase()
    return activeOrderResults.filter((o) => {
      const order = orderById[o.order_id]
      // Filter by due_date range: only include orders with due_date within startDate ~ endDate+14 days
      if (order) {
        if (order.due_date < rangeStart || order.due_date > rangeEnd) return false
      }
      // If no matching order found, exclude (order without due_date cannot be validated)
      if (!order) return false
      if (orderStatusFilter && o.status !== orderStatusFilter) return false
      if (skuLower && !o.sku_id.toLowerCase().includes(skuLower)) return false
      return true
    }).sort((a, b) => {
      const orderA = orderById[a.order_id]
      const orderB = orderById[b.order_id]
      const dateA = orderA?.due_date ?? ''
      const dateB = orderB?.due_date ?? ''
      return dateA.localeCompare(dateB)
    })
  }, [activeOrderResults, orderById, week.startDate, rangeEnd, orderStatusFilter, skuSearch])

  const handleExportToExcel = useCallback(() => {
    const doExport = () => {
      excelWriteDialog.open(result.cell_instructions)
    }
    if (result.warnings.length > 0) {
      confirmDialog({
        message: `${result.warnings.length}件の警告があります。このまま書き戻しますか？`,
        header: '確認',
        icon: 'pi pi-exclamation-triangle',
        accept: doExport,
      })
      return
    }
    doExport()
  }, [result.warnings.length, result.cell_instructions, excelWriteDialog])

  const changeCell = (task: ScheduledTask_Output) => {
    const ct = getChangeType(task)
    if (ct === 'unchanged') return null
    if (ct === 'pulled_forward')
      return (
        <span className="text-blue-600 text-xs">
          ← 前倒し ({task.original_date}から)
        </span>
      )
    if (ct === 'pushed_back')
      return (
        <span className="text-orange-600 text-xs">
          → 後送 ({task.original_date}から)
        </span>
      )
    if (task.split_from){
      const fromTask = taskById[task.split_from]
      return (
        <span className="text-yellow-700 text-xs">
          分割 (元タスク: {fromTask?.original_date ?? '繰越タスク'})
        </span>
      )
    }else{
      const toTask = Object.values(taskById).find(t => t.split_from === task.task_id)
      if (!toTask) return (
        <span className="text-blue-600 text-xs">
          ← 前倒し ({task.original_date}の一部)
        </span>
      )
      return (
        <span className="text-yellow-700 text-xs">
          分割 (分割先：{toTask?.original_date ?? '不明'})
        </span>
      )
    }
  }

  const changeBg = (task: ScheduledTask_Output): string => {
    const ct = getChangeType(task)
    if (ct === 'pulled_forward') return 'bg-blue-50'
    if (ct === 'pushed_back') return 'bg-orange-50'
    if (ct === 'split') return 'bg-yellow-50'
    return ''
  }

  const getMachineDayStats = useCallback(
    (date: string, machineId: string) => {
      const dateAssignment = assignments[date]
      const shifts: ShiftType[] = ['day', 'evening', 'night']
      const staffParts: string[] = []
      let activeShifts = 0

      for (const shift of shifts) {
        const slotStaff = dateAssignment?.[shift]?.[machineId] ?? []
        const names = slotStaff.filter((s): s is string => !!s)
        if (names.length > 0) {
          staffParts.push(`${SHIFT_LABELS[shift]}：${names.join('、')}`)
          activeShifts++
        }
      }

      const opHours = activeShifts * 8
      const actualSec = (tasksByDateMachine[date]?.[machineId] ?? []).reduce(
        (sum, t) => sum + t.duration_sec,
        0,
      )
      const utilization = opHours > 0 ? Math.round((actualSec / (opHours * 3600)) * 100) : null
      const utilization24h = Math.round((actualSec / (24 * 3600)) * 100)

      return {
        staffParts,
        opHours,
        actualSec,
        actualLabel: formatDuration(actualSec),
        utilization,
        utilization24h,
      }
    },
    [assignments, tasksByDateMachine],
  )

  const daySummaryData = useMemo(() => {
    if (!daySummaryDate) return null
    const date = daySummaryDate
    const machines = sortedMachineIds(date)
    const machineStats = machines.map((mid) => ({
      machineId: mid,
      ...getMachineDayStats(date, mid),
    }))

    let totalBoxes = 0
    let totalPl = 0
    const materialUsage: Record<string, number> = {}

    for (const mid of machines) {
      const tasks = tasksByDateMachine[date]?.[mid] ?? []
      for (const task of tasks) {
        const p = productBySku[task.sku_id]
        if (!p) continue
        // 箱/PL は出荷可能な良品数ベースで算出する。
        if (p.quantity_per_box) totalBoxes += Math.ceil(task.quantity / p.quantity_per_box)
        if (p.quantity_per_pl) totalPl += Math.ceil(task.quantity / p.quantity_per_pl)
        if (p.material_per_unit_kg != null && p.material1) {
          // 材料消費は不良分も含めた総生産量ベースで集計する。
          const kg = task.quantity_total * p.material_per_unit_kg
          materialUsage[p.material1] = (materialUsage[p.material1] ?? 0) + kg
        }
      }
    }

    return { date, machineStats, totalBoxes, totalPl, materialUsage }
  }, [daySummaryDate, sortedMachineIds, getMachineDayStats, tasksByDateMachine, productBySku])

  // その日・その成形機のスタッフ割当が、直近の最適化時から変更されているか
  const isMachineDayChanged = useCallback(
    (date: string, machineId: string): boolean => {
      if (!optimizedAssignments) return false
      const shifts: ShiftType[] = ['day', 'evening', 'night']
      const normalize = (slots: (string | null)[] | undefined): string =>
        (slots ?? []).map((v) => (typeof v === 'string' ? v : '')).join('|').replace(/\|+$/, '')
      return shifts.some(
        (shift) =>
          normalize(assignments[date]?.[shift]?.[machineId]) !==
          normalize(optimizedAssignments[date]?.[shift]?.[machineId]),
      )
    },
    [assignments, optimizedAssignments],
  )

  const openStaffEdit = useCallback((date: string, dateLabel: string, machineId: string) => {
    setStaffEditTarget({ date, dateLabel, machineId })
  }, [])

  const handleApplyStaffEdit = useCallback(
    (date: string, machineId: string, slotsByShift: SlotsByShift) => {
      onUpdateMachineDayAssignments(date, machineId, slotsByShift)
    },
    [onUpdateMachineDayAssignments],
  )

  // シフト帳票を別ウィンドウで開いて印刷する（帳票の中身は PrintView.tsx に分離）
  const handlePrint = useCallback(() => {
    const note = noteRef.current?.value ?? ''
    const ok = openShiftPrintWindow({
      week,
      machineIds,
      tasksByDateMachine,
      sequencesByDateMachine,
      assignments,
      staffList,
      note,
    })
    if (!ok) alert('印刷ウィンドウを開けませんでした。ポップアップブロックを解除してください。')
  }, [week, machineIds, tasksByDateMachine, sequencesByDateMachine, assignments, staffList])

  return (
    <div className="mt-4">
      <ConfirmDialog />

      {/* View mode toggle */}
      {hasBaseline && (
        <div className="flex align-items-center gap-2 mb-3">
          <span className="text-sm font-semibold text-600">表示:</span>
          <SelectButton
            value={viewMode}
            onChange={(e) => { if (e.value) setViewMode(e.value) }}
            options={[
              { label: '最適化後', value: 'optimized' },
              { label: '元計画', value: 'baseline' },
            ]}
            optionLabel="label"
            optionValue="value"
            allowEmpty={false}
          />
          {viewMode === 'baseline' && (
            <Tag value="元計画表示中" severity="warning" className="ml-2" />
          )}
        </div>
      )}

      {/* シフト割当が最適化後に変更された場合の再最適化促し */}
      {hasPendingShiftChanges && (
        <Message
          severity="warn"
          className="w-full mb-3 justify-content-start"
          content={
            <div className="flex align-items-center gap-3 flex-wrap w-full">
              <i className="pi pi-exclamation-triangle" />
              <span className="text-sm flex-1">
                スタッフ割当が最適化後に変更されています。帳票には反映済みですが、稼働時間の変化により最適化結果（生産計画）が変わる可能性があります。「再最適化」を実行して結果を更新してください。
              </span>
              <Button
                label="再最適化"
                icon="pi pi-refresh"
                severity="warning"
                size="small"
                onClick={onReoptimize}
                loading={isOptimizing}
              />
            </div>
          }
        />
      )}

      {/* Summary bar */}
      <div className="flex align-items-center gap-3 mb-4 flex-wrap">
        <div className="flex gap-3 flex-1 flex-wrap">
          <div className="border-1 border-round p-3 text-center" style={{ minWidth: '120px' }}>
            <div className="text-500 text-sm mb-1">平均充足率</div>
            <div className="text-2xl font-bold">{avgUtilization}%</div>
          </div>
          <div className="border-1 border-round p-3 text-center" style={{ minWidth: '120px' }}>
            <div className="text-500 text-sm mb-1">平均稼働率 (24h基準)</div>
            <div className="text-2xl font-bold">{avgUtilization24h}%</div>
          </div>
          <div className="border-1 border-round p-3 text-center" style={{ minWidth: '120px' }}>
            <div className="text-500 text-sm mb-1">充足注文</div>
            <div className="text-2xl font-bold text-blue-600">{filteredSummary.fulfilled}件</div>
          </div>
          <div className="border-1 border-round p-3 text-center" style={{ minWidth: '120px' }}>
            <div className="text-500 text-sm mb-1">一部充足</div>
            <div className="text-2xl font-bold text-yellow-600">{filteredSummary.partial}件</div>
          </div>
          <div className="border-1 border-round p-3 text-center" style={{ minWidth: '120px' }}>
            <div className="text-500 text-sm mb-1">未処理</div>
            <div className="text-2xl font-bold text-red-600">{filteredSummary.unmatched}件</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button label="シフト割当に戻る" icon="pi pi-arrow-left" severity="secondary" size="small" onClick={onBack} />
          <Button
            label="印刷"
            icon="pi pi-print"
            severity="help"
            size="small"
            onClick={handlePrint}
            tooltip="シフト帳票をA4横で印刷します"
          />
          <Button
            label="再最適化"
            icon="pi pi-refresh"
            severity={hasPendingShiftChanges ? 'warning' : 'info'}
            size="small"
            onClick={onReoptimize}
            loading={isOptimizing}
            tooltip={hasPendingShiftChanges ? 'スタッフ割当が変更されています。再最適化で結果を更新してください' : undefined}
          />
          <Button
            label="Excelから再読込"
            icon="pi pi-sync"
            severity="warning"
            size="small"
            onClick={excelReloadDialog.open}
            loading={excelReloadDialog.isExcelReloading}
            tooltip="OneDriveからExcelを再読込し、同じ設定で再最適化します"
          />
          <Button
            label="Excelに書戻"
            icon="pi pi-file-excel"
            severity="success"
            size="small"
            onClick={handleExportToExcel}
          />
        </div>
      </div>

      {/* Main tabs */}
      <TabView>
        {/* Tab 1: 日別生産計画 */}
        <TabPanel header="日別生産計画">
          {allDates.map((date) => {
            const d = new Date(date)
            const dateLabel = `${date.slice(5).replace(/-/g, '/')} (${WEEKDAY_LABELS[d.getDay()]})`
            const dayMachines = sortedMachineIds(date)

            const rows = buildDayRows(date)

            return (
              <Panel
                key={date}
                header={
                  <div className="flex align-items-center gap-3 flex-wrap">
                    <span className="font-semibold">{dateLabel}</span>
                    <Button
                      label="この日のサマリ"
                      icon="pi pi-chart-bar"
                      size="small"
                      text
                      onClick={(e) => { e.stopPropagation(); setDaySummaryDate(date) }}
                    />
                  </div>
                }
                toggleable
                className="mb-3"
              >
                <DataTable
                  value={rows}
                  size="small"
                  showGridlines
                  rowGroupMode="subheader"
                  groupRowsBy="machineGroup"
                  rowGroupHeaderTemplate={(rowData: DayRow) => {
                    const mid = rowData.machineGroup
                    const { staffParts, opHours, actualSec, actualLabel, utilization, utilization24h } = getMachineDayStats(date, mid)
                    const machineWarnings = warningsByDateMachine[`${date}|${mid}`] ?? []
                    const staffChanged = isMachineDayChanged(date, mid)
                    return (
                      <div className="flex align-items-center gap-3 flex-wrap">
                        <span className="font-bold text-sm text-red-500">{mid}</span>
                        {machineWarnings.length > 0 && (
                          <span
                            className="cursor-pointer inline-flex align-items-center gap-1"
                            onClick={(e) => {
                              setActiveWarnings(machineWarnings)
                              warningOverlayRef.current?.toggle(e)
                            }}
                          >
                            <i className={`pi ${machineWarnings.some((w) => w.warning_type === 'deadline_exceeded') ? 'pi-exclamation-circle text-red-500' : 'pi-exclamation-triangle text-orange-500'}`} />
                            <span className="text-xs text-orange-600 font-semibold">{machineWarnings.length}</span>
                          </span>
                        )}
                        {staffParts.length > 0 && (
                          <span className="text-xs text-600">
                            {staffParts.join('　')}
                          </span>
                        )}
                        {opHours > 0 && (
                          <span className="text-xs font-semibold text-primary">{opHours}h</span>
                        )}
                        {actualSec > 0 && (
                          <span className={`text-xs font-semibold ${opHours > 0 && actualSec > opHours * 3600 ? 'text-red-500' : 'text-green-600'}`}>
                            実績 {actualLabel}
                          </span>
                        )}
                        {utilization != null && (
                          <span className={`text-xs font-semibold ${utilization >= 95 ? 'text-red-500' : utilization === 0 ? 'text-orange-400' : 'text-green-600'}`}>
                            充足率 {utilization}%
                          </span>
                        )}
                        {actualSec > 0 && (
                          <span className={`text-xs font-semibold ${utilization24h >= 95 ? 'text-red-500' : 'text-600'}`}>
                            稼働率 {utilization24h}%
                          </span>
                        )}
                        {staffChanged && (
                          <Tag value="割当変更あり（要再最適化）" severity="warning" className="text-xs" />
                        )}
                        <Button
                          label="スタッフ割当の修正"
                          icon="pi pi-user-edit"
                          size="small"
                          text
                          className="p-0 text-xs"
                          onClick={(e) => {
                            e.stopPropagation()
                            openStaffEdit(date, dateLabel, mid)
                          }}
                        />
                      </div>
                    )
                  }}
                >
                  <Column
                    field="sku_id"
                    header="製品"
                    style={{ width: '24rem' }}
                    body={(row: DayRow) => {
                      if ('isIdle' in row && row.isIdle) {
                        return <span className="text-sm text-red-800 bg-red-100">生産なし</span>
                      }
                      const p = productBySku[row.sku_id]
                      const variant = p?.machine_variants?.[row.machine_id]
                      return (
                        <div>
                          <a
                            className="font-semibold text-sm cursor-pointer text-primary hover:underline"
                            onClick={(e) => {
                              e.stopPropagation()
                              setTaskDetailInfo({ task: row, product: p, variant })
                            }}
                          >
                            {row.sku_id}
                          </a>
                          {p && <div className="text-xs text-500">{p.category}</div>}
                        </div>
                      )
                    }}
                  />
                  <Column
                    field="quantity"
                    header="数量"
                    style={{ width: '80px' }}
                    body={(row: DayRow) => ('isIdle' in row && row.isIdle ? <span className="text-400">—</span> : row.quantity.toLocaleString('en-US'))}
                  />
                  <Column
                    field="duration_sec"
                    header="時間"
                    style={{ width: '80px' }}
                    body={(row: DayRow) => ('isIdle' in row && row.isIdle ? <span className="text-400">—</span> : formatDuration(row.duration_sec))}
                  />
                  <Column
                    header="変更"
                    style={{ width: '200px' }}
                    hidden={viewMode === 'baseline'}
                    body={(row: DayRow) =>
                      'isIdle' in row && row.isIdle ? (
                        <span className="text-400 text-sm">—</span>
                      ) : (
                        <div className={`px-1 border-round ${changeBg(row)}`}>{changeCell(row)}</div>
                      )
                    }
                  />
                  <Column
                    header="充当注文"
                    body={(row: DayRow) =>
                      'isIdle' in row && row.isIdle ? (
                        <span className="text-500 text-xs">生産予定なし</span>
                      ) : row.matched_orders.length === 0 ? (
                        <span className="text-500 text-xs">なし</span>
                      ) : (
                        <div className="flex flex-column gap-0">
                          {row.matched_orders.map((m) => (
                            <span key={m.order_id} className="text-xs">
                              {m.order_id}: {m.allocated_quantity.toLocaleString('en-US')}
                            </span>
                          ))}
                        </div>
                      )
                    }
                  />
                </DataTable>
              </Panel>
            )
          })}

          {/* Unscheduled tasks */}
          {result.unscheduled_tasks.length > 0 && (
            <Panel
              header={`未スケジュールタスク (${result.unscheduled_tasks.length}件) — 次週以降に繰り越し`}
              toggleable
              className="mb-3"
            >
              <DataTable value={result.unscheduled_tasks} size="small" showGridlines>
                <Column field="machine_id" header="成形機" style={{ width: '140px' }} />
                <Column
                  field="sku_id"
                  header="製品"
                  body={(row: { sku_id: string }) => {
                    const p = productBySku[row.sku_id]
                    return (
                      <div>
                        <div className="font-semibold text-sm">{row.sku_id}</div>
                        {p && <div className="text-xs text-500">{p.category}</div>}
                      </div>
                    )
                  }}
                />
                <Column
                  field="quantity"
                  header="数量"
                  style={{ width: '80px' }}
                  body={(row: { quantity: number }) => row.quantity.toLocaleString('en-US')}
                />
                <Column
                  header="理由"
                  body={(row: { task_id: string }) => {
                    const warns = warningsByTaskId[row.task_id] ?? []
                    return warns.length > 0 ? (
                      <span className="text-xs text-orange-600">{warns.map((w) => w.message).join(' / ')}</span>
                    ) : (
                      <span className="text-xs text-500">—</span>
                    )
                  }}
                />
              </DataTable>
            </Panel>
          )}
        </TabPanel>

        {/* Tab 2: 注文照合 */}
        <TabPanel header="注文照合">
          <div className="flex align-items-center gap-2 mb-3 flex-wrap">
              <InputText
                value={skuSearch}
                onChange={(e) => setSkuSearch(e.target.value)}
                placeholder="品番で絞込"
                size={16}
              />
            <span className="text-sm text-600">ステータス:</span>
            {[null, 'fulfilled', 'partial', 'unmatched'].map((s) => (
              <Button
                key={String(s)}
                label={s == null ? 'すべて' : ORDER_STATUS_LABEL[s]}
                size="small"
                severity={s == null ? 'secondary' : ORDER_STATUS_SEVERITY[s]}
                outlined={orderStatusFilter !== s}
                onClick={() => setOrderStatusFilter(s)}
              />
            ))}
          </div>
          <DataTable value={filteredOrderResults} size="small" showGridlines stripedRows>
            <Column field="order_id" header="注文ID" className="text-xs" style={{ width: '300px' }} />
            <Column
              header="納期"
              style={{ width: '100px' }}
              body={(row: OrderMatchResult) => {
                const order = orderById[row.order_id]
                return order ? (
                  <span className="text-sm">{order.due_date.replace(/-/g, '/')}</span>
                ) : (
                  <span className="text-400 text-sm">—</span>
                )
              }}
            />
            <Column
              field="sku_id"
              header="製品"
              body={(row: OrderMatchResult) => {
                const p = productBySku[row.sku_id]
                return (
                  <div>
                    <div className="font-semibold text-sm">{row.sku_id}</div>
                    {p && <div className="text-xs text-500">{p.category}</div>}
                  </div>
                )
              }}
            />
            <Column
              field="total_quantity"
              header="注文数"
              style={{ width: '80px' }}
              body={(row: OrderMatchResult) => row.total_quantity.toLocaleString('en-US')}
            />
            <Column
              field="allocated_quantity"
              header="充足数"
              style={{ width: '80px' }}
              body={(row: OrderMatchResult) => row.allocated_quantity.toLocaleString('en-US')}
            />
            <Column
              field="remaining_quantity"
              header="残数"
              style={{ width: '80px' }}
              body={(row: OrderMatchResult) => (
                <span className={row.remaining_quantity > 0 ? 'text-red-500 font-semibold' : ''}>
                  {row.remaining_quantity.toLocaleString('en-US')}
                </span>
              )}
            />
            <Column
              field="status"
              header="ステータス"
              style={{ width: '100px' }}
              body={(row: OrderMatchResult) => (
                <Tag
                  value={ORDER_STATUS_LABEL[row.status] ?? row.status}
                  severity={ORDER_STATUS_SEVERITY[row.status] ?? 'info'}
                />
              )}
            />
            <Column
              header="詳細"
              style={{ width: '80px' }}
              body={(row: OrderMatchResult) => (
                <Button
                  icon="pi pi-list"
                  size="small"
                  text
                  onClick={() => setOrderDetailResult(row)}
                />
              )}
            />
          </DataTable>
        </TabPanel>
      </TabView>

      {/* Warning panel */}
      {result.warnings.length > 0 && (
        <Panel
          header={`警告 (${result.warnings.length}件)`}
          toggleable
          collapsed
          className="mt-4"
        >
          <div className="flex flex-column gap-2">
            {result.warnings.map((w, i) => (
              <div key={i} className="flex align-items-start gap-2">
                <span
                  className={`text-xs font-bold ${w.warning_type === 'deadline_exceeded' ? 'text-red-500' : 'text-orange-500'}`}
                >
                  {w.warning_type === 'deadline_exceeded' ? '🔴' : '⚠'}
                </span>
                <span className="text-sm">{w.message}</span>
                {w.task_id && (
                  <span className="text-xs text-500 ml-1">({w.task_id})</span>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* AI思考ログ */}
      {(result.debug_logs?.length ?? 0) > 0 && (
        <Panel
          header={`AI思考ログ (${result.debug_logs.length}件)`}
          toggleable
          collapsed
          className="mt-4"
        >
          <div className="mb-3">
            <InputText
              value={debugLogSearch}
              onChange={(e) => setDebugLogSearch(e.target.value)}
              placeholder="ログを検索..."
              className="w-full"
            />
          </div>
          <div
            className="flex flex-column gap-1 overflow-y-auto"
            style={{ maxHeight: '400px', fontFamily: 'monospace', fontSize: '0.8rem' }}
          >
            {result.debug_logs
              .filter((log) => {
                if (!debugLogSearch) return true
                return log.toLowerCase().includes(debugLogSearch.toLowerCase())
              })
              .map((log, i) => {
                const isWarning = log.startsWith('WARNING')
                return (
                  <div
                    key={i}
                    className={`px-2 py-1 border-round ${
                      isWarning ? 'bg-orange-50 text-orange-800' : 'bg-gray-50 text-700'
                    }`}
                  >
                    {log}
                  </div>
                )
              })}
          </div>
        </Panel>
      )}

      {/* 特記入力 & 印刷 */}
      <Panel header="特記・印刷" toggleable className="mt-4">
        <div className="flex flex-column gap-3">
          <div>
            <label className="block text-sm font-semibold text-600 mb-2">特記（帳票の特記欄に印刷されます）</label>
            <InputTextarea
              ref={noteRef}
              rows={4}
              autoResize
              className="w-full"
              placeholder="帳票右端の特記欄に印刷する内容を入力してください"
            />
          </div>
          <div>
            <Button
              label="シフト帳票を印刷"
              icon="pi pi-print"
              severity="help"
              onClick={handlePrint}
            />
          </div>
        </div>
      </Panel>

      {/* Order detail dialog */}
      <Dialog
        header={orderDetailResult ? `${orderDetailResult.order_id} 詳細` : ''}
        visible={!!orderDetailResult}
        style={{ width: '480px' }}
        onHide={() => setOrderDetailResult(null)}
        modal
        draggable={false}
      >
        {orderDetailResult && (
          <div className="flex flex-column gap-3">
            <div>
              <span className="text-600 text-sm">製品: </span>
              <span className="font-semibold">{orderDetailResult.sku_id}</span>
              {productBySku[orderDetailResult.sku_id] && (
                <span className="text-500 text-sm ml-2">
                  {productBySku[orderDetailResult.sku_id].name}
                </span>
              )}
            </div>
            <div className="flex gap-4 text-sm">
              <div>
                <span className="text-600">注文数: </span>
                <span>{orderDetailResult.total_quantity.toLocaleString('en-US')}</span>
              </div>
              <div>
                <span className="text-600">充足数: </span>
                <span>{orderDetailResult.allocated_quantity.toLocaleString('en-US')}</span>
              </div>
              <div>
                <span className="text-600">残数: </span>
                <span className={orderDetailResult.remaining_quantity > 0 ? 'text-red-500 font-semibold' : ''}>
                  {orderDetailResult.remaining_quantity.toLocaleString('en-US')}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm font-semibold mb-2 text-700">充当タスク</div>
              {orderDetailResult.allocated_from.length === 0 ? (
                <p className="text-500 text-sm">充当タスクなし</p>
              ) : (
                <div className="flex flex-column gap-1">
                  {orderDetailResult.allocated_from.map((alloc: TaskAllocation) => {
                    const task = taskById[alloc.task_id]
                    const ct = task ? getChangeType(task) : 'unchanged'
                    const ctLabel =
                      ct === 'pulled_forward'
                        ? ' [← 前倒し]'
                        : ct === 'pushed_back'
                          ? ' [→ 後送]'
                          : ct === 'split'
                            ? ' [分割]'
                            : ''
                    return (
                      <div key={alloc.task_id} className="text-sm flex align-items-center gap-2">
                        <span className="text-500">├</span>
                        <span>
                          {alloc.task_id} ({alloc.scheduled_date},{' '}
                          {task?.machine_id ?? '?'}) → {alloc.quantity.toLocaleString('en-US')}個
                          {ctLabel && <span className="text-blue-600 ml-1">{ctLabel}</span>}
                        </span>
                      </div>
                    )
                  })}
                  {orderDetailResult.remaining_quantity > 0 && (
                    <div className="text-sm text-orange-600 ml-3">
                      残 {orderDetailResult.remaining_quantity.toLocaleString('en-US')}個 → 次週繰り越し
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>

      {/* Excel reload progress dialog */}
      <Dialog
        header="OneDrive再読み込み"
        visible={excelReloadDialog.visible}
        style={{ width: '50rem' }}
        closable={!excelReloadDialog.isExcelReloading}
        onHide={excelReloadDialog.close}
        footer={<Button label="閉じる" onClick={excelReloadDialog.close} disabled={excelReloadDialog.isExcelReloading} />}
      >
        <ul className="list-none m-0 p-0 flex flex-col gap-3">
          {excelReloadDialog.progress.map((step) => {
            const iconClass = {
              pending: 'pi pi-clock text-gray-400',
              active: 'pi pi-spin pi-spinner text-blue-500',
              completed: 'pi pi-check-circle text-green-500',
              error: 'pi pi-times-circle text-red-500',
            }[step.status]
            return (
              <li key={step.id} className="flex items-center gap-3">
                <div className="mt-2">
                  <i className={iconClass} style={{ fontSize: '1.25rem' }} />
                </div>
                <div>
                  <p className="m-0 font-medium">{step.label}</p>
                  {step.status === 'active' && <small className="text-gray-500">処理中...</small>}
                  {step.status === 'error' && <small className="text-red-500">エラーが発生しました</small>}
                </div>
              </li>
            )
          })}
        </ul>
        {excelReloadDialog.stage === 'success' && (
          <p className="text-green-600 mt-4 mb-0">最新データの取り込みが完了しました。</p>
        )}
        {excelReloadDialog.error && (
          <p className="text-red-500 mt-4 mb-0">{excelReloadDialog.error}</p>
        )}
      </Dialog>

      {/* Excel write dialog */}
      <Dialog
        header="Excelに書き戻し"
        visible={excelWriteDialog.visible}
        style={{ width: '32rem' }}
        closable={!excelWriteDialog.isWriting}
        onHide={excelWriteDialog.close}
        footer={<Button label="閉じる" onClick={excelWriteDialog.close} disabled={excelWriteDialog.isWriting} />}
      >
        <p className="text-sm text-600 mb-3">
          Excelを開いたままだと書き込みに失敗します。ファイルを閉じてから実行してください。
        </p>
        {excelWriteDialog.isLocked && (
          <Message
            severity="warn"
            text="ファイルが他のアプリで開かれています。Excelを閉じてから再度お試しください。"
            className="mb-3"
          />
        )}
        <div className="mb-3">
          {excelWriteDialog.isWriting ? (
            <ProgressBar mode="indeterminate" />
          ) : excelWriteDialog.progress !== null ? (
            <ProgressBar value={Math.max(0, Math.min(100, excelWriteDialog.progress))} showValue />
          ) : null}
          {excelWriteDialog.lastLog && (
            <small className="block mt-2 text-600">{excelWriteDialog.lastLog}</small>
          )}
        </div>
        {excelWriteDialog.isSuccess && (
          <>
            <Message severity="success" text="Excelファイルへの書き戻しが完了しました" className="mt-3" />
            <p className="text-sm text-600 mt-2">
              OneDrive上のファイルを開いて反映内容を確認してください。
            </p>
          </>
        )}
        {excelWriteDialog.error && (
          <Message severity="error" text={excelWriteDialog.error} className="mt-3" />
        )}
      </Dialog>

      {/* Day Summary Dialog */}
      <Dialog
        header={daySummaryData ? (() => {
          const d = new Date(daySummaryData.date)
          return `${daySummaryData.date.slice(5).replace(/-/g, '/')} (${WEEKDAY_LABELS[d.getDay()]}) サマリ`
        })() : ''}
        visible={!!daySummaryDate}
        style={{ width: '560px' }}
        onHide={() => setDaySummaryDate(null)}
        modal
        draggable={false}
      >
        {daySummaryData && (() => {
          const maxUtil = Math.max(...daySummaryData.machineStats.map((s) => s.utilization ?? 0), 1)
          const materialEntries = Object.entries(daySummaryData.materialUsage).sort((a, b) => b[1] - a[1])
          const maxMatKg = Math.max(...materialEntries.map(([, v]) => v), 1)

          return (
            <div className="flex flex-column gap-4">
              {/* Machine fulfillment (shift-based) bar chart */}
              <div>
                <div className="text-sm font-semibold mb-2 text-700">成形機 充足率</div>
                <div className="flex flex-column gap-2">
                  {daySummaryData.machineStats.map(({ machineId, utilization, opHours, actualLabel }) => {
                    const pct = utilization ?? 0
                    const barColor = pct >= 95 ? '#ef4444' : pct === 0 ? '#fb923c' : '#22c55e'
                    const labelColor = pct >= 95 ? 'text-red-500' : pct === 0 ? 'text-orange-400' : 'text-green-600'
                    return (
                      <div key={machineId} className="flex align-items-center gap-2">
                        <span className="text-sm font-semibold" style={{ width: '5rem', flexShrink: 0 }}>{machineId}</span>
                        <div className="flex-1 border-round overflow-hidden" style={{ height: '18px', background: '#e5e7eb' }}>
                          {utilization != null ? (
                            <div
                              style={{
                                width: `${(pct / Math.max(maxUtil, 100)) * 100}%`,
                                height: '100%',
                                background: barColor,
                                minWidth: pct > 0 ? '2px' : '0',
                                transition: 'width 0.3s',
                              }}
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: '#d1d5db' }} />
                          )}
                        </div>
                        <span className={`text-xs font-semibold ${labelColor}`} style={{ width: '3.5rem', textAlign: 'right', flexShrink: 0 }}>
                          {utilization != null ? `${pct}%` : '割当なし'}
                        </span>
                        {opHours > 0 && (
                          <span className="text-xs text-500" style={{ width: '4.5rem', flexShrink: 0 }}>
                            {actualLabel}/{opHours}h
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Machine utilization (24h-based) bar chart */}
              <div>
                <div className="text-sm font-semibold mb-2 text-700">成形機 稼働率 (24h基準)</div>
                <div className="flex flex-column gap-2">
                  {daySummaryData.machineStats.map(({ machineId, utilization24h, actualLabel }) => {
                    const barColor = utilization24h >= 95 ? '#ef4444' : utilization24h === 0 ? '#fb923c' : '#3b82f6'
                    const labelColor = utilization24h >= 95 ? 'text-red-500' : utilization24h === 0 ? 'text-orange-400' : 'text-blue-600'
                    return (
                      <div key={machineId} className="flex align-items-center gap-2">
                        <span className="text-sm font-semibold" style={{ width: '5rem', flexShrink: 0 }}>{machineId}</span>
                        <div className="flex-1 border-round overflow-hidden" style={{ height: '18px', background: '#e5e7eb' }}>
                          <div
                            style={{
                              width: `${utilization24h}%`,
                              height: '100%',
                              background: barColor,
                              minWidth: utilization24h > 0 ? '2px' : '0',
                              transition: 'width 0.3s',
                            }}
                          />
                        </div>
                        <span className={`text-xs font-semibold ${labelColor}`} style={{ width: '3.5rem', textAlign: 'right', flexShrink: 0 }}>
                          {utilization24h}%
                        </span>
                        <span className="text-xs text-500" style={{ width: '4.5rem', flexShrink: 0 }}>
                          {actualLabel}/24h
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Box / PL totals */}
              <div>
                <div className="text-sm font-semibold mb-2 text-700">梱包サマリ</div>
                <div className="flex gap-4 text-sm">
                  <div className="border-1 border-round p-3 text-center flex-1">
                    <div className="text-500 text-xs mb-1">箱数 (合計)</div>
                    <div className="text-xl font-bold">{daySummaryData.totalBoxes.toLocaleString('en-US')}</div>
                  </div>
                  <div className="border-1 border-round p-3 text-center flex-1">
                    <div className="text-500 text-xs mb-1">PL数 (合計)</div>
                    <div className="text-xl font-bold">{daySummaryData.totalPl.toLocaleString('en-US')}</div>
                  </div>
                </div>
              </div>

              {/* Material usage bar chart */}
              {materialEntries.length > 0 && (
                <div>
                  <div className="text-sm font-semibold mb-2 text-700">材料使用量</div>
                  <div className="flex flex-column gap-2">
                    {materialEntries.map(([mat, kg]) => (
                      <div key={mat} className="flex align-items-center gap-2">
                        <span className="text-sm" style={{ width: '8rem', flexShrink: 0 }}>{mat}</span>
                        <div className="flex-1 border-round overflow-hidden" style={{ height: '18px', background: '#e5e7eb' }}>
                          <div
                            style={{
                              width: `${(kg / maxMatKg) * 100}%`,
                              height: '100%',
                              background: '#6366f1',
                              minWidth: '2px',
                            }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-700" style={{ width: '5rem', textAlign: 'right', flexShrink: 0 }}>
                          {kg.toFixed(1)} kg
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })()}
      </Dialog>

      {/* Warning overlay panel for machine headers */}
      <OverlayPanel ref={warningOverlayRef} dismissable style={{ maxWidth: '480px' }}>
        {activeWarnings.length > 0 && (
          <div className="flex flex-column gap-2">
            <div className="font-semibold text-sm mb-1">警告 ({activeWarnings.length}件)</div>
            {activeWarnings.map((w, i) => (
              <div key={i} className="flex align-items-start gap-2">
                <span className={`text-xs font-bold mt-1 ${w.warning_type === 'deadline_exceeded' ? 'text-red-500' : 'text-orange-500'}`}>
                  {w.warning_type === 'deadline_exceeded' ? '🔴' : '⚠'}
                </span>
                <div>
                  <div className="text-sm">{w.message}</div>
                  {w.task_id && <div className="text-xs text-500">タスク: {w.task_id}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </OverlayPanel>

      {/* スタッフ割当の修正ダイアログ */}
      <StaffAssignmentEditDialog
        target={staffEditTarget}
        staffList={staffList}
        assignments={assignments}
        tasks={staffEditTarget ? tasksByDateMachine[staffEditTarget.date]?.[staffEditTarget.machineId] ?? [] : []}
        productBySku={productBySku}
        onApply={handleApplyStaffEdit}
        onHide={() => setStaffEditTarget(null)}
      />

      {/* Task Detail Dialog */}
      <Dialog
        header={taskDetailInfo ? `${taskDetailInfo.task.sku_id} - 製品詳細` : ''}
        visible={!!taskDetailInfo}
        style={{ width: '560px' }}
        onHide={() => setTaskDetailInfo(null)}
        modal
        draggable={false}
      >
        {taskDetailInfo && (() => {
          const { task, product, variant } = taskDetailInfo
          const defectRate = product?.defect_rate ?? 0
          const expectedDefects = Math.max(task.quantity_total - task.quantity, 0)
          const materialPerUnit = product?.material_per_unit_kg ?? null
          const totalMaterialKg = materialPerUnit != null ? task.quantity_total * materialPerUnit : null
          const qtyPerBox = product?.quantity_per_box ?? null
          const qtyPerPl = product?.quantity_per_pl ?? null
          const boxCount = qtyPerBox ? Math.ceil(task.quantity / qtyPerBox) : null
          const plCount = qtyPerPl ? Math.ceil(task.quantity / qtyPerPl) : null

          const fmt = (v: number | null | undefined) => {
            if (v == null) return '-'
            return Number.isFinite(v) ? v.toLocaleString('en-US') : '-'
          }
          const fmtF = (v: number | null | undefined, digits = 1) => {
            if (v == null) return '-'
            return Number.isFinite(v) ? v.toFixed(digits) : '-'
          }
          const fmtBool = (v: boolean | null | undefined) => {
            if (v == null) return '-'
            return v ? '〇' : '×'
          }

          const liddedLabel =
            product?.is_lidded_or_appearance === 'lidded' ? '便蓋品' :
            product?.is_lidded_or_appearance === 'appearance' ? '外観品' :
            product?.is_lidded_or_appearance != null ? product.is_lidded_or_appearance : '未分類'

          return (
            <div className="flex flex-column gap-3">
              {/* Product info */}
              {product && (
                <div className="flex flex-column gap-1">
                  <div className="text-sm text-500">{product.category}</div>
                  <div className="font-semibold">{product.name}</div>
                  <div className="flex gap-2 align-items-center mt-1">
                    <span className="text-xs text-600">分類:</span>
                    <span className="text-xs font-medium">{liddedLabel}</span>
                  </div>
                </div>
              )}

              {/* Production task info */}
              <div>
                <div className="text-sm font-semibold mb-1 text-700">生産タスク</div>
                <div className="grid text-sm">
                  <div className="col-6 flex justify-content-between"><span className="text-600">成形機</span><span>{task.machine_id}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">予定日</span><span>{task.scheduled_date}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">元日付</span><span>{task.original_date}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">生産数</span><span>{fmt(task.quantity_total)}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">良品数</span><span>{fmt(task.quantity)}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">不良率</span><span>{fmtF(defectRate * 100)}%</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">予測不良数</span><span>{fmt(expectedDefects)}</span></div>
                  <div className="col-6 flex justify-content-between"><span className="text-600">生産時間</span><span>{formatDuration(task.duration_sec)}</span></div>
                  {task.is_split && (
                    <div className="col-12 flex justify-content-between"><span className="text-600">分割元</span><span>{task.split_from ?? '-'}</span></div>
                  )}
                </div>
              </div>

              {/* Material / packaging calc */}
              {product && (
                <div>
                  <div className="text-sm font-semibold mb-1 text-700">材料・梱包</div>
                  <div className="grid text-sm">
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料1</span><span>{product.material1 ?? '-'}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料2</span><span>{product.material2 ?? '-'}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料原単位(kg)</span><span>{fmtF(materialPerUnit, 4)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">材料使用量(kg)</span><span>{fmtF(totalMaterialKg, 1)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">入数/箱</span><span>{fmt(qtyPerBox)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">箱数</span><span>{fmt(boxCount)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">入数/PL</span><span>{fmt(qtyPerPl)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">PL数</span><span>{fmt(plCount)}</span></div>
                  </div>
                </div>
              )}

              {/* Machine variant info */}
              {variant && (
                <div>
                  <div className="text-sm font-semibold mb-1 text-700">成形機情報 ({variant.machine_id})</div>
                  <div className="grid text-sm">
                    <div className="col-6 flex justify-content-between"><span className="text-600">サイクルタイム(s)</span><span>{fmtF(variant.cycle_time_sec)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">組立工程</span><span>{fmtBool(variant.requires_assembly)}</span></div>
                    <div className="col-6 flex justify-content-between"><span className="text-600">二台検査可</span><span>{fmtBool(variant.is_dual_inspection_compatible)}</span></div>
                    {variant.note && (
                      <div className="col-12">
                        <span className="text-600">成形方法：</span>
                        <span className="text-700">{variant.note}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })()}
      </Dialog>
    </div>
  )
}

export default OptimizerResultView
